import { useState, FormEvent, useEffect } from "react";
import { useAuthStore } from '../../store/authStore'
import OAuthButton from "./OAuthButton";
import { EyeIcon, EyeSlashIcon, UserCircleIcon } from '@heroicons/react/24/solid';
import { isElectron } from "../../utils/electron";

interface LoginViewProps {
  setView: (view: string) => void;
  onClose: () => void;
  invitation_code?: string;
}

const LoginView = ({ setView, onClose, invitation_code }: LoginViewProps) => {
  const [viewPwd, setViewPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuthStore()

  useEffect(() => {
    if (isElectron()) {
      const savedUsername = localStorage.getItem("username");
      if (savedUsername && window.electron?.getPassword) {
        window.electron.getPassword(savedUsername).then((savedPassword:string) => {
          const emailInput = document.getElementById("username") as HTMLInputElement;
          const passwordInput = document.getElementById("password") as HTMLInputElement;
          if (emailInput && passwordInput && savedPassword) {
            emailInput.value = savedUsername;
            passwordInput.value = savedPassword;
          }
        });
      }
    }
  }, []);


  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    setError("");
    const username = formData.get("username") as string;
    const password = formData.get("password") as string;
    const remember = formData.get("remember") as string;
    if (!username) {
      setError("Email is required.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }
    if (remember) localStorage.setItem("username",username);
    if (remember && isElectron() && window.electron?.savePassword) {
      await window.electron.savePassword(username, password);
    }
    setSubmitting(true);
    try {
      await login({ email: username, password });
      onClose();
    } catch (err: any) {
      setError(err.error || err.message || "Authentication error.");
    } finally {
      setSubmitting(false);
    }
  };

  const onOauthError = (_error: string) => setError(_error);

  return <form className="w-full" onSubmit={handleSubmit}>
    <div className="flex justify-center mb-2 text-gray-500">
      <UserCircleIcon className="w-8 h-8" />
    </div>
    <h1 className="text-2xl px-5 full-width text-center">Sign In</h1>
    {error && <div className="py-1 px-4 my-2 text-xs bg-red-100 text-red-700 rounded">{error}</div>}
    <input
      placeholder="Enter your email"
      id="username"
      name="username"
      type="email"
      value={localStorage.getItem("username") ?? ""}
      disabled={submitting}
      className="my-3 w-full p-2 border border-gray-300 dark:bg-gray-900 rounded-md"
    />
    <div className="relative my-3">
      <input
        placeholder="Enter your password"
        id="password"
        name="password"
        type={viewPwd ? "text" : "password"}
        disabled={submitting}
        className="w-full p-2 border border-gray-300 dark:bg-gray-900 rounded-md"
      />
      <button type="button" className="absolute right-2 top-2" onClick={() => setViewPwd(!viewPwd)} tabIndex={-1}>
        {viewPwd ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
      </button>
    </div>
    <div className="pt-3 text-sm">
      <input type="checkbox" id="remember" name="remember" disabled={submitting} className="mr-2 " />
      <label htmlFor="remember">Remember Me</label>
    </div>
    <button type="submit" disabled={submitting} className="w-full bg-blue-600 text-white rounded-md text-sm h-10 mt-5">
      Sign In
    </button>
    <OAuthButton provider="google" disabled={submitting} consent={true} onError={onOauthError} className="h-10 my-2" />
    <div className="flex justify-between pt-2 text-sm">
      <button className="text-gray-600 cursor-pointer hover:underline" onClick={() => setView("forgot")} disabled={submitting}>
        Forgot Password?
      </button>
      {([true, "true", 1, "1"].includes(import.meta.env.VITE_ALLOW_SIGNUP) || invitation_code) &&
        <button className="text-gray-600 cursor-pointer hover:underline" onClick={() => setView("signup")} disabled={submitting}>
          Sign Up
        </button>
      }
    </div>
  </form>
};

export default LoginView;
